/**
 * Every Web Storage key the app uses, in one place.
 *
 * Keys are dot-segmented (`declunk.<namespace>.<name>`) so a whole namespace can be dropped
 * with a prefix scan rather than a hand-maintained list that drifts from the keys it claims
 * to cover. A key carries the area it lives in, so no call site ever picks between
 * `localStorage` and `sessionStorage` — `StorageService` reads it off the key.
 */

export type StorageArea = 'local' | 'session';

export interface StorageKey {
  readonly name: string;
  readonly area: StorageArea;
}

/** A key prefix, in a key's shape, for removing a whole namespace. */
export type StorageKeyPrefix = StorageKey;

export function localKey(name: string): StorageKey {
  return { name, area: 'local' };
}

export function sessionKey(name: string): StorageKey {
  return { name, area: 'session' };
}

/**
 * Keys with no account data behind them: tokens and the device-wide preferences.
 * Safe to read before the session exists.
 */
export const STORAGE_KEYS = {
  auth: {
    access: localKey('declunk.auth.access'),
    refresh: localKey('declunk.auth.refresh'),
    webflowReturnUrl: localKey('declunk.auth.webflow_return_url'),
    /** Handed from the collaborative-signup landing to onboarding; dies with the tab. */
    collaborativeSignupToken: sessionKey('declunk.auth.collaborative_signup_token'),
  },
  integrations: {
    /**
     * The project a HubSpot install was started from. HubSpot's return carries no project id,
     * unlike Slack's, so it is kept across the trip; dies with the tab.
     */
    hubspotReturnProjectId: sessionKey('declunk.integrations.hubspot_return_project_id'),
  },
  pref: {
    /**
     * Deliberately not user-scoped: it reveals nothing about the previous account, and it is
     * the most visible layout shift if it can only be read once the session has arrived.
     */
    sidebarCollapsed: localKey('declunk.pref.sidebar_collapsed'),
    companyTermsSeen: localKey('declunk.pref.company_terms_seen'),
  },
} as const;

/**
 * Preferences that carry account data, scoped by user id so signing in as someone else on the
 * same browser does not inherit the previous account's choices.
 *
 * Every one of these keys needs the session before it can be built, so every read of one sits
 * behind session-ready and applies after first paint. `userPreference()` is the shape that does
 * that without a lifecycle hook per call site.
 */
export const userPrefKey = {
  selectedProjects: (userId: string) => localKey(`${userScope(userId)}pref.selected_projects`),
  pinnedProjects: (userId: string) => localKey(`${userScope(userId)}pref.pinned_projects`),
  recentSearches: (userId: string) => localKey(`${userScope(userId)}pref.recent_searches`),
  /** The project last opened on the Beta Dashboard, reopened on the next visit. Not the navbar selector's. */
  dashboardProject: (userId: string) => localKey(`${userScope(userId)}pref.dashboard_project`),
  requestsViewMode: (userId: string, projectId: string) =>
    localKey(`${projectScope(userId, projectId)}requests_view_mode`),
  requestFilters: (userId: string, projectId: string) => localKey(`${projectScope(userId, projectId)}request_filters`),
  kanbanHiddenStatuses: (userId: string, projectId: string) =>
    localKey(`${projectScope(userId, projectId)}kanban_hidden_statuses`),
  requestDraft: (userId: string, projectId: string) => localKey(`${projectScope(userId, projectId)}request_draft`),
};

function userScope(userId: string): string {
  return `declunk.u.${userId}.`;
}

function projectScope(userId: string, projectId: string): string {
  return `${userScope(userId)}pref.project.${projectId}.`;
}

/**
 * Every user-scoped preference at once, for the one case no builder can name: a user id that
 * changes shape, orphaning keys keyed by the old one. Deliberately not called from `logout()` —
 * preferences survive logout by design, which is the entire point of scoping them by account.
 */
export const ALL_USER_PREFS: StorageKeyPrefix = localKey('declunk.u.');
